'use client';

import { useEffect, useState } from 'react';
import React from 'react';
import Image from 'next/image';
import styled, { keyframes } from 'styled-components';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import FAQSection from '@/components/FAQSection';
import SupportSection from '@/components/SupportSection';
import { GetData } from '@/utils/request';
import { statics } from '@/data/store';
import { COLORS } from '@/constants/colors';
import { crossReferenceFiltersFaqs } from '@/data/faqData';
import { formatMakeName } from '@/utils/formatters';

const TEXT_DARK = '#333';
const BUTTON_HEIGHT_DESKTOP = 40;

interface GreenFilterProduct {
  id: number;
  title?: string;
  gfu_part_num?: string;
}

interface CompetitorProduct {
  id: number;
  competitor_part_number: string;
  compatitor?: {
    id: number;
    title: string;
  };
}

export default function CrossReferenceFiltersPage() {
  const [loading, setLoading] = useState(false);
  const [greenFilters, setGreenFilters] = useState<GreenFilterProduct[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<GreenFilterProduct | null>(null);
  const [filterCodes, setFilterCodes] = useState<CompetitorProduct[]>([]);
  const [showModel, setShowModel] = useState(false);

  useEffect(() => {
    getGreenFilters();
  }, []);

  useEffect(() => {
    if (selectedFilter) {
      getFilterCodes();
    }
  }, [selectedFilter]);

  const getGreenFilters = async () => {
    setLoading(true);
    let tableDataT: GreenFilterProduct[] = [];
    const response = await GetData(
      `${statics.BaseUrl}/product?pageNo=1&pageSize=10000&is_active=1`,
      200,
      null
    );
    if (response.ResponseCode === 'Success') {
      tableDataT = response?.data?.rows;
    }
    setGreenFilters(tableDataT);
    setLoading(false);
  };

  const onChangeGreenFilter = (id: string) => {
    setShowModel(false);
    setSelectedFilter(null);
    const selectedFilterT = greenFilters.filter((item) => item.id === parseInt(id));

    if (selectedFilterT.length) {
      setSelectedFilter(selectedFilterT[0]);
    }
  };

  const getFilterCodes = async () => {
    let tableDataT: CompetitorProduct[] = [];
    const response = await GetData(
      `${statics.BaseUrl}/competitor-products?product_id=${selectedFilter?.id}`,
      200,
      null
    );
    if (response.ResponseCode === 'Success') {
      tableDataT = response?.data;
    }
    setFilterCodes(tableDataT);
    setShowModel(true);
  };

  return (
    <>
      <Header handleClickIndex={() => {}} />
      <div className="header-gap-creator"></div>

      <main>
      <Breadcrumbs items={[{ label: 'Green Filter to Other Brand' }]} />
      {loading && (
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      )}

      <Section>
        <Container>
          <HeadingBlock>
            <Headline>Green Filter Cross Reference</Headline>
            <Accent />
            <IntroText>
              Use the Green Filter cross-reference tool to compare a Green High Performance Air
              Filter with compatible replacement numbers from other brands. Select your Green Filter
              part number to view available interchange options.
            </IntroText>
          </HeadingBlock>

          <ContentBlock>
            <SectionTitle>Find Cross-Reference Filters by Green Filter Part Number</SectionTitle>
            <SectionDescription>
              This filter conversion chart helps you compare Green Filter part numbers with other
              aftermarket air filter brands. Use it to check possible replacement options from other
              brands when available.
            </SectionDescription>
          </ContentBlock>

          <ContentGrid>
            <ImageContainer>
              <Image src="/assets/filters.webp" alt="Green Filter Products" width={520} height={317} />
            </ImageContainer>

            <FormCard>
              <FormInstruction>
                Select a Green Filter part number to search for matching cross-reference filters from
                other brands.
              </FormInstruction>

              <FormInnerCard>
                <FormRow>
                  <SelectWrapper>
                    <label htmlFor="green-filter-select">Green Filter</label>
                    <StyledSelect
                      id="green-filter-select"
                      value={selectedFilter?.id?.toString() || ''}
                      onChange={(e) => onChangeGreenFilter(e.target.value)}
                    >
                      <option value="">Select</option>
                      {greenFilters &&
                        greenFilters.map((item) => {
                          if (item.gfu_part_num) {
                            return (
                              <option key={item.id} value={item.id.toString()}>
                                {item.gfu_part_num}
                              </option>
                            );
                          }
                          return null;
                        })}
                    </StyledSelect>
                  </SelectWrapper>
                </FormRow>
              </FormInnerCard>
            </FormCard>
          </ContentGrid>

          {showModel && filterCodes.length > 0 && (
            <ResultSection>
              <ResultCard>
                <ResultTable>
                  <thead>
                    <tr>
                      <th>Brand</th>
                      <th>Filter Number</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterCodes.map((item) => (
                      <tr key={item.id}>
                        <td>{formatMakeName(item?.compatitor?.title) || 'N/A'}</td>
                        <td>{item?.competitor_part_number}</td>
                      </tr>
                    ))}
                  </tbody>
                </ResultTable>
              </ResultCard>
            </ResultSection>
          )}
        </Container>
      </Section>

      <FAQSection
        faqs={crossReferenceFiltersFaqs}
        title="FAQs About Green Filter Cross Reference"
      />

      <SupportSection
        text="Need help finding a replacement or interchange filter? Use our vehicle selector or contact Green Filter support for expert assistance."
        primaryButtonText="Shop by Vehicle"
        primaryButtonLink="/shop-by-vehicle"
        secondaryButtonText="Contact Support"
        secondaryButtonLink="/contact-us"
      />
      </main>

      <Footer />
    </>
  );
}

// Styled Components

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  border: 6px solid rgba(0, 173, 35, 0.1);
  border-top: 6px solid ${COLORS.brandGreen};
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: ${spin} 1s linear infinite;
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  label {
    font-family: Lato, sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: ${TEXT_DARK};
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  font-family: Lato, sans-serif;
  font-size: 14px;
  color: ${TEXT_DARK};
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  outline: none;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: ${COLORS.brandGreen};
  }

  &:focus {
    border-color: ${COLORS.brandGreen};
    box-shadow: 0 0 0 3px rgba(0, 173, 35, 0.1);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.6;
  }

  option {
    padding: 8px;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const Section = styled.section`
  background: #fafafa;
  padding: 80px 24px;
  @media (max-width: 767px) {
    padding: 48px 16px;
  }
`;

const Container = styled.div`
  max-width: 1140px;
  margin: 0 auto;
`;

const HeadingBlock = styled.div`
  max-width: 640px;
  margin: 0 auto 56px;
  text-align: center;
  @media (max-width: 767px) {
    margin-bottom: 40px;
  }
`;

const Headline = styled.h1`
  font-family: Lato, sans-serif;
  font-size: 32px;
  font-weight: 800;
  color: ${TEXT_DARK};
  margin: 0;
  line-height: 1.2;
  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

const Accent = styled.div`
  width: 56px;
  height: 3px;
  background: ${COLORS.brandGreen};
  border-radius: 2px;
  margin: 14px auto 16px;
`;

const IntroText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 16px;
  line-height: 1.6;
  color: #555;
  margin: 0;
  max-width: 720px;
  margin: 0 auto;
  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const ContentBlock = styled.div`
  max-width: 960px;
  margin: 0 auto 32px;
  text-align: center;
  @media (max-width: 767px) {
    margin-bottom: 24px;
  }
`;

const SectionTitle = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: ${TEXT_DARK};
  margin: 0 0 12px;
  line-height: 1.3;
  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const SectionDescription = styled.p`
  font-family: Lato, sans-serif;
  font-size: 15px;
  line-height: 1.6;
  color: #555;
  margin: 0;
  max-width: 800px;
  margin: 0 auto;
  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const FormInstruction = styled.p`
  font-family: Lato, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #666;
  margin: 0 0 16px;
  @media (max-width: 767px) {
    font-size: 13px;
  }
`;

const ContentGrid = styled.div`
  max-width: 960px;
  margin: 0 auto 40px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  padding: 32px;
  display: grid;
  grid-template-columns: 400px 1fr;
  gap: 40px;
  align-items: center;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    gap: 32px;
    padding: 28px;
  }

  @media (max-width: 767px) {
    padding: 24px 20px;
  }
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f8f8f8;
  border-radius: 8px;

  img {
    width: 100%;
    max-width: 100%;
    height: auto;
    object-fit: contain;
  }

  @media (max-width: 991px) {
    padding: 28px;
  }

  @media (max-width: 767px) {
    padding: 20px;
  }
`;

const FormCard = styled.div`
  /* Container for the form */
`;

const FormInnerCard = styled.div`
  background: #f8f8f8;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid #ebebeb;
  @media (max-width: 767px) {
    padding: 20px;
  }
`;

const FormRow = styled.div`
  .MuiFormControl-root {
    width: 100%;
  }
  .MuiInputLabel-root {
    font-family: Lato, sans-serif;
  }
  .MuiInputLabel-root.Mui-focused {
    color: ${COLORS.brandGreen};
  }
`;

const ResultSection = styled.div`
  margin-top: 40px;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 767px) {
    max-width: 100%;
    margin-left: -16px;
    margin-right: -16px;
  }
`;

const ResultCard = styled.div`
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  @media (max-width: 767px) {
    border-radius: 0;
    border-left: none;
    border-right: none;
  }
`;

const ResultTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: Lato, sans-serif;

  thead {
    background: ${COLORS.brandGreen};
    color: #fff;
  }

  th {
    padding: 16px 20px;
    text-align: left;
    font-weight: 600;
    font-size: 14px;
    letter-spacing: 0.5px;
    text-transform: uppercase;

    @media (max-width: 767px) {
      padding: 12px 16px;
      font-size: 13px;
    }
  }

  tbody tr {
    border-bottom: 1px solid #f0f0f0;
    transition: background-color 0.2s ease;

    &:nth-child(even) {
      background-color: #f8f8f8;
    }

    &:hover {
      background-color: rgba(0, 173, 35, 0.05);
    }

    &:last-child {
      border-bottom: none;
    }
  }

  td {
    padding: 16px 20px;
    font-size: 15px;
    color: #333;

    @media (max-width: 767px) {
      padding: 12px 16px;
      font-size: 14px;
    }
  }

  @media (max-width: 767px) {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

